import React from 'react';
import numeral from 'numeral';
import { titleize, singularize, humanize } from 'inflection';

import { sliders } from 'buyside/util/ideaFilterConstants';
import { communityRatings, sortOrders } from 'buyside/util/ideaFilterConstants';
import {
  yearsOperating,
  quartiles,
  timeframes,
  managementFee,
  incentiveFee,
  sortBy,
  visibleResearchOptions
} from 'buyside/util/fundProfileFilterConstants';

import styles from './userSavedSearchesPageStyles';

const { labelizedEmployerProfileTags } = require('buyside/util/constantsFromRails.json');

const formatRangeWithIncrement = (increment, range) => {
  const [start, end] = range.split('_');
  const result = [];

  if (!start) {
    result.push(increment[0]);
  } else {
    result.push(start);
  }

  if (!end) {
    result.push(increment[increment.length - 1]);
  } else {
    result.push(end);
  }

  return result;
};

const formatBigNumberRange = (increment, range) => {
  const result = formatRangeWithIncrement(increment, range);
  return result.map(val => numeral(val).format('0a')).join('-');
};

const formatSellsideConsensus = (increment, range) =>
  formatRangeWithIncrement(increment, range).join('-');

const formatNumberRange = (increment, range) => {
  const [, end] = range.split('_');
  const hasEnd = !!end;
  const [formattedStart, formattedEnd] = formatRangeWithIncrement(increment, range);

  return [formattedStart, hasEnd ? formattedEnd : `${formattedEnd}+`].join('-');
};

const formatRange = range => {
  const [start, end] = range.split('_');

  if (start && !end) {
    return `${start}+`;
  } else if (!start && end) {
    return `0-${end}`;
  }

  return range.replace('_', '-');
};

const formatCredit = credit => {
  const [first, second] = credit.split('_');

  return [first || 'NR', second || 'AAA'].join('-');
};

const formatQuartiles = searchValue => {
  return quartiles.find(({ value }) => value === searchValue).label;
};

const formatArray = (searchKey, searchValue) => {
  const formattedValues = searchValue.map(val => titleize(humanize(val))).join(', ');
  const formattedSearchKey = searchValue.length > 1 ? searchKey : singularize(searchKey);

  return `${formattedSearchKey}: ${formattedValues}`;
};

const transformers = {
  amount_outstanding: searchValue =>
    `Amount Outstanding: ${formatBigNumberRange(
      sliders.amount_outstanding.increments,
      searchValue
    )}`,
  annualized_return: searchValue =>
    `IRR: ${formatNumberRange(sliders.returns.increments, searchValue)}`,
  asset_classes: searchValue => `${formatArray('Asset Classes', searchValue)}`,
  aum_range: searchValue => `AUM: ${searchValue}`,
  beta_metric: searchValue => `Beta: ${formatQuartiles(searchValue)}`,
  by_currently_ranked: () => 'Currently Ranked Analyst Ideas Only',
  channel_check_types: searchValue => `${formatArray('Channel Checks', searchValue)}`,
  catalysts: searchValue => `${formatArray('Catalysts', searchValue)}`,
  city: searchValue => `City: ${searchValue}`,
  comments_count: searchValue => `Number of Comments: ${formatRange(searchValue)}`,
  community_rating: searchValue =>
    `Community Rating: ${formatNumberRange(communityRatings, searchValue)}`,
  completeness: searchValue => `Min. Completeness: ${searchValue}`,
  country: searchValue => `Country: ${searchValue}`,
  current_pe: searchValue =>
    `Current P/E: ${formatNumberRange(sliders.current_pe.increments, searchValue)}`,
  div_yield: searchValue =>
    `Dividend Yield: ${formatNumberRange(sliders.div_yield.increments, searchValue)}`,
  ev_ltm_ebitda: searchValue =>
    `EV/LTM EBITDA: ${formatNumberRange(sliders.ev_ltm_ebitda.increments, searchValue)}`,
  ev_fcf: searchValue => `EV/LTM FCF: ${formatNumberRange(sliders.ev_fcf.increments, searchValue)}`,
  exact_phrase_match: () => 'Exact Keyword Match: Off',
  expected_return: searchValue =>
    `Min. Expected Return: ${formatNumberRange(sliders.returns.increments, searchValue)}`,
  fcf_yield: searchValue =>
    `FCF YIELD: ${formatNumberRange(sliders.fcf_yield.increments, searchValue)}`,
  fitch_credit_rating: searchValue => `Fitch Rating: ${formatCredit(searchValue)}`,
  financial_model_types: searchValue => `${formatArray('Financial Models', searchValue)}`,
  formatted_thesis: () => 'Formatted Thesis Only',
  forward_pe: searchValue =>
    `Forward P/E: ${formatNumberRange(sliders.forward_pe.increments, searchValue)}`,
  founders_shares: () => 'Founders Shares',
  fund_focus: searchValue => `Focus: ${searchValue}`,
  fund_strategy: searchValue => `Strategy: ${searchValue}`,
  fund_sectors: searchValue => `${formatArray('Fund Sectors', searchValue)}`,
  fund_tags: searchValues =>
    `${searchValues.length > 1 ? 'Tags' : 'Tag'}: ${searchValues
      .map(
        searchValue => labelizedEmployerProfileTags.find(({ value }) => value === searchValue).label
      )
      .join(', ')}`,
  fund_type: searchValue => `Firm Type: ${searchValue}`,
  geographies: searchValue => `${formatArray('Geographies', searchValue)}`,
  has_completed_due_diligence: () => 'Has Completed Due Diligence',
  has_listed_holdings: () => 'Have Completed Due Diligence Section',
  has_supplied_references: () => 'Have Supplied References',
  highwater_mark: () => 'Highwater Mark',
  hurdle_rate: () => 'Hurdle Rate',
  industries: searchValue => `${formatArray('Industries', searchValue)}`,
  incentive_fee: searchValue =>
    `Incentive Fee: ${incentiveFee.find(({ value }) => value === searchValue).label}`,
  include_closed: () => 'Open Ideas Only',
  investor_level_gates: () => 'Investor-Level Gates',
  lock_up: () => 'Lock Up',
  management_fee: searchValue =>
    `Management Fee: ${managementFee.find(({ value }) => value === searchValue).label}`,
  market_cap: searchValue =>
    `Market Cap (USD): ${formatBigNumberRange(sliders.market_cap.increments, searchValue)}`,
  maturity_date_from: searchValue => `Maturity Date From: ${searchValue}`,
  maturity_date_to: searchValue => `Maturity Date To: ${searchValue}`,
  moody_credit_rating: searchValue => `Moody Rating: ${formatCredit(searchValue)}`,
  open_access_ideas: () => 'Open Access Ideas Only',
  opened_after: searchValue => `Opened After: ${searchValue}`,
  opened_before: searchValue => `Opened Before: ${searchValue} `,
  portfolio_percentage: searchValue => `Portfolio Percentage: ${searchValue}`,
  ranking_categories: searchValue => `${formatArray('Ranking Categories', searchValue)}`,
  recommendation: searchValue => `Direction: ${searchValue === 'long' ? 'Longs' : 'Shorts'}`,
  research_available: searchValue =>
    `Research Available: ${
      visibleResearchOptions
        .concat([
          { value: 'one_to_three_ideas', label: '1-2 Ideas' },
          { value: 'three_to_ten_ideas', label: '3-9 Ideas' }
        ])
        .find(({ value }) => value === searchValue).label
    }`,
  return_quartile: searchValue => `Quartile: ${formatQuartiles(searchValue)}`,
  return_timeframe: searchValue =>
    `Timeframe: ${timeframes.find(({ value }) => value === searchValue).label}`,
  return_vs_benchmark: searchValue =>
    `Return vs Benchmark: ${formatNumberRange(sliders.returns.increments, searchValue)}`,
  secondary_research_types: searchValue => `${formatArray('Secondary Research', searchValue)}`,
  sectors: searchValue => `${formatArray('Sectors', searchValue)}`,
  seed_deal: () => 'Seed Deals',
  sellside_consensus: searchValue =>
    `Sellside Consensus: ${formatSellsideConsensus(
      sliders.sellside_consensus.increments,
      searchValue
    )}`,
  seniority: searchValue => `${formatArray('Seniority', searchValue)}`,
  sharpe_metric: searchValue => `Sharpe Ratio: ${formatQuartiles(searchValue)}`,
  situations: searchValue =>
    `Investment Type: ${searchValue.map(val => titleize(val.split('_').join(' '))).join(', ')}`,
  sort_by: searchValue => `Sort by: ${sortOrders.find(({ value }) => value === searchValue).label}`,
  sort_funds_by: searchValue =>
    `Sort By: ${sortBy.find(({ value }) => value === searchValue).label}`,
  standard_credit_rating: searchValue => `S&P Rating: ${formatCredit(searchValue)}`,
  supporting_documents: searchValue => `${formatArray('Supprting Documents', searchValue)}`,
  text: searchValue => `Keyword Search: ${searchValue}`,
  ticker: searchValue => `Ticker Search: ${searchValue}`,
  timeframe: searchValue => `Expected Timeframe: ${humanize(searchValue)}`,
  total_return: searchValue =>
    `Return To Date: ${formatNumberRange(sliders.returns.increments, searchValue)}`,
  turnover_metric: searchValue => `Turnover Ratio: ${formatQuartiles(searchValue)}`,
  usd_ev: searchValue =>
    `EV (USD): ${formatBigNumberRange(sliders.usd_ev.increments, searchValue)}`,
  user_ratings_count: searchValue => `Number of Ratings: ${formatRange(searchValue)}`,
  views_category: searchValue => `Number of Views: ${titleize(searchValue)}`,
  volume: searchValue =>
    `Three Mo. Adv (USD): ${formatBigNumberRange(sliders.volume.increments, searchValue)}`,
  weekly_top_ideas: () => 'Weekly Top Ideas Only',
  years_operating: searchValue =>
    `Years Operating: ${yearsOperating.find(({ value }) => value === searchValue).label}`
};

const CriteriaFormatter = ({ criteriaObj }) => (
  <div className={styles.criteria}>
    {Object.entries(criteriaObj)
      .map(([searchField, searchValue]) => {
        const transformer = transformers[searchField];
        return transformer ? transformer(searchValue) : '';
      })
      .filter(Boolean)
      .sort()
      .join('; ')}
  </div>
);

export default CriteriaFormatter;
